<template>
  <BaseModal
    v-if="templates"
    :id="id"
    hide-footer
    class="modal--add-slide"
    :focus-trap-active="!nestedModalIsOpen"
    :initial-focus="initialFocus"
    :heading="draftType === 'board' ? $t('Add Page') : $t('Add Slide')"
    :on-close="cancel"
  >
    <div class="add-slide-modal">
      <LoadingIndicator
        v-if="loading"
        :text="$t('Adding slide...')"
        class="add-slide-modal__loading"
        absolute
      />

      <div
        v-if="draftIsLesson"
        class="add-slide-modal__subheading"
      >
        <h4>{{ $t('Media') }}</h4>
        <NebulaTooltip
          position="bottom"
          :text="$t('Add media from Discovery Education or your own device as full slides')"
        >
          <NebulaIcon symbol-id="information" />
        </NebulaTooltip>
      </div>

      <div
        v-if="draftIsLesson"
        class="add-slide-modal__media-list"
      >
        <NebulaButton
          ref="searchButton"
          icon-left="search"
          size="s"
          :text="$t('Search DE')"
          type="flat"
          @click="openSearchAssetsModal"
        />
        <NebulaButton
          icon-left="upload"
          size="s"
          :text="$t('Upload from My Device')"
          type="flat"
          @click="openMyContentUploadModal"
        />
        <!-- Adding from My Content is temporarily removed per STUD-1621 -->
        <NebulaButton
          v-if="false"
          icon-left="drive"
          size="s"
          :text="$t('From My Content')"
          type="flat"
          @click="openMyContentModal"
        />
        <NebulaButton
          icon-left="quicklist"
          size="s"
          :text="$t('From Favorites')"
          type="flat"
          @click="openQuicklistModal"
        />
      </div>

      <div
        v-if="draftIsLesson"
        class="add-slide-modal__subheading"
      >
        <h4>{{ $t('Asset ID') }}</h4>
        <NebulaTooltip
          position="bottom"
          :text="$t('Add a full slide asset by entering the asset id')"
        >
          <NebulaIcon symbol-id="information" />
        </NebulaTooltip>
      </div>

      <div v-if="draftIsLesson">
        <form
          class="add-slide-modal__quiz-item-form"
          @submit="addFullSlideFromAssetId"
        >
          <NebulaFieldGroup
            input-id="add-slide-modal-asset-id"
            helper-icon="warning--filled"
            :helper-status="assetError ? 'error' : null"
            :helper-text="assetError"
          >
            <template #formElement>
              <NebulaInput
                id="add-slide-modal-asset-id"
                size="m"
                :has-helper="!!assetError"
                :status="assetError ? 'error' : null"
                :aria-label="$t('Enter an asset id')"
                :placeholder="$t('Enter an asset id')"
                :text="assetId"
                @input="assetId = $event"
              />
            </template>
          </NebulaFieldGroup>

          <NebulaButton
            size="m"
            :text="$t('Add Slide')"
            :disabled="loading"
            :is-disabled="loading"
            is-submit
          />
        </form>
      </div>

      <div
        v-if="draftIsLesson"
        class="add-slide-modal__subheading"
      >
        <h4>{{ $t('Layout') }}</h4>
        <NebulaTooltip
          position="bottom"
          :text="$t('Add a blank or pre-formatted slide')"
        >
          <NebulaIcon symbol-id="information" />
        </NebulaTooltip>
      </div>

      <ul class="add-slide-modal__list">
        <li class="add-slide-modal__item">
          {{ draftType === 'board' ? $t('Blank Page') : $t('Blank Slide') }}
          <button
            ref="blankSlideButton"
            class="add-slide-modal__preview-wrapper"
            @click="addBlankSlide"
          >
            <img
              src="@/assets/templates/blank.png"
              class="add-slide-modal__preview-image"
              :alt="draftType === 'board'
                ? $t('Preview of Blank Page')
                : $t('Preview of Blank Slide')"
            >
          </button>
        </li>
        <li
          v-for="template in pageTemplates"
          :key="template.id"
          class="add-slide-modal__item"
        >
          {{ $t(template.name) }}
          <button
            class="add-slide-modal__preview-wrapper"
            @click="selectTemplate(template)"
          >
            <img
              :src="imageUrl(template)"
              class="add-slide-modal__preview-image"
              :alt="templateName(template.name)"
            >
          </button>
        </li>
      </ul>
    </div>
  </BaseModal>
</template>

<script>
/* eslint-disable import/no-dynamic-require */
import {
  NebulaButton,
  NebulaIcon,
  NebulaTooltip,
  NebulaFieldGroup,
  NebulaInput,
} from '@discoveryedu/nebula-components';
import { cloneDeep, get, omit } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import BaseModal from '@/components/modals/BaseModal.vue';
import {
  widthToPixels,
  heightToPixels,
  xPositionToPixels,
  yPositionToPixels,
  assignUniqueZPositions,
} from '@/lib/utils/grid';
import { isGuid } from '@/lib/utils';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import * as types from '@/lib/constants/store';
import {
  useAssetStore,
  useEditorStore,
  useModalStore,
} from '@/stores';

export default {
  name: 'AddSlideModal',
  components: {
    BaseModal,
    LoadingIndicator,
    NebulaButton,
    NebulaIcon,
    NebulaTooltip,
    NebulaInput,
    NebulaFieldGroup,
  },
  props: {
    id: {
      type: String, // used for opening and closing modal
      required: true,
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      // for keeping track of where to restore focus to when a submodal is closed
      previousFocus: null,
      assetId: '',
      assetError: null,
      loading: false,
    };
  },
  computed: {
    ...mapState(useEditorStore, [
      'draftIsLesson',
      'draftType',
      'templates',
    ]),
    ...mapState(useModalStore, [
      'modals',
      'myContentModalOptions',
      'region',
    ]),
    initialFocus() {
      // Focus trap expects to receive a function that returns an element
      return () => (this.previousFocus
        || this.$refs.searchButton?.$el
        || this.$refs.blankSlideButton);
    },
    nestedModalIsOpen() {
      return this.modals.length > 1 || get(this, 'myContentModalOptions.config.isActive');
    },
    pageTemplates() {
      // Only show page-level templates, and leave out the Activity template
      return this.templates.filter((template) => (
        template.template_type === 'page' && template.name !== 'Activity'
      ));
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_PAGE,
    ]),
    ...mapActions(useModalStore, [
      types.CLOSE_MODAL,
      types.OPEN_MODAL,
      types.SET_MY_CONTENT_MODAL_OPTIONS,
    ]),
    ...mapActions(useAssetStore, [
      types.GET_ASSET,
    ]),
    async addFullSlideFromAssetId(event) {
      event.preventDefault();
      this.assetError = null;
      const assetId = (this.assetId || '').trim();
      if (!isGuid(assetId)) {
        this.assetError = this.$t('Please enter a valid asset id');
        return;
      }

      try {
        this.loading = true;
        const { asset, error } = await this[types.GET_ASSET]({ assetId });
        if (error) throw error;
        if (asset) {
          this[types.ADD_PAGE]({
            page: {
              options: {
                asset_id: assetId,
              },
              title: asset?.title,
            },
          });
          this[types.CLOSE_MODAL]({ id: this.id });
        } else {
          this.assetError = this.$t('Asset not found');
        }
      } catch (error) {
        this.assetError = get(error, 'response.data.meta.message', error.message);
      } finally {
        this.loading = false;
      }
    },
    addBlankSlide() {
      this[types.ADD_PAGE]();
      this.onCloseModal();
    },
    cancel() {
      if (this.nestedModalIsOpen) return;
      this.onCloseModal();
    },
    templateName(tn) {
      return this.$t('Preview of %(templateName)s', { templateName: this.$t(tn) });
    },
    onChooseAssets(assets) {
      // This is used as a callback from the asset selection modals.
      // Selected assets are returned as an array, but we only expect one
      const assetId = assets[0]?.id;
      this[types.ADD_PAGE]({
        page: {
          options: {
            asset_id: assetId,
          },
          title: assets[0]?.title,
        },
      });
      this[types.CLOSE_MODAL]({ id: this.id });
    },
    onCloseModal() {
      this[types.CLOSE_MODAL]({ id: this.id });
    },
    openMyContentModal(e) {
      this.previousFocus = e.target;

      this[types.SET_MY_CONTENT_MODAL_OPTIONS]({
        config: {
          hideDistrictTab: false, // see DEX-2646; this method is only used in the US
          isActive: true,
          isStudent: false, // this method is not used by students
          selectMultiple: false,
        },
        onChooseAssets: this.onChooseAssets,
      });
    },
    openMyContentUploadModal(e) {
      this.previousFocus = e.target;

      this[types.OPEN_MODAL]({
        type: 'EditorialUploadInfo',
        allowMultiple: false,
        onConfirm: this.onChooseAssets,
      });
    },
    openQuicklistModal(e) {
      this.previousFocus = e.target;

      this[types.OPEN_MODAL]({
        type: 'FromQuicklist',
        onConfirm: this.onChooseAssets,
        singleSelect: true,
      });
    },
    openSearchAssetsModal(e) {
      this.previousFocus = e.target;

      this[types.OPEN_MODAL]({
        type: 'SearchAssets',
        onConfirm: this.onChooseAssets,
        singleSelect: true,
      });
    },
    selectTemplate(template) {
      // Grab the first page
      const page = cloneDeep(template.pages[0]);

      // Add notes if missing
      if (!page.notes) {
        page.notes = [];
      }

      /*
        If page.options are not set, ensure that default page options are preserved.
        If page.options is 'null' in the template, it will overwrite any defaults when
        the page is added, so we need to set to 'undefined' so this doesn't happen.
      */
      if (!page.options) {
        page.options = undefined;
      }

      page.modules = page.modules.map((mod) => {
        let mappedMod = mod;
        /*
          If the current draft is a slideshow or lesson, then its pages will have the
          page.options.grid_disabled = 'true'. This means we need to convert layout
          dimensions in the template from rows/columns to pixels.
        */
        mappedMod.layout.width = widthToPixels(mappedMod.layout.width);
        mappedMod.layout.height = heightToPixels(mappedMod.layout.height);
        mappedMod.layout.x_position = xPositionToPixels(mappedMod.layout.x_position);
        mappedMod.layout.y_position = yPositionToPixels(mappedMod.layout.y_position);

        // For quiz items, add prompt text if missing
        if (['prompt', 'single_selection', 'multiple_selection', 'block'].includes(mappedMod.type)) {
          const formattedMod = {
            ...mappedMod,
            options: mappedMod.options || {},
          };

          if (!formattedMod.options.content) {
            formattedMod.options.content = this.$t('Type in question here');
          }

          mappedMod = formattedMod;
        }

        return mappedMod;
      });

      page.modules = assignUniqueZPositions(page.modules);

      // Templates can have dummy page titles, which we want to strip
      this[types.ADD_PAGE]({ page: omit(page, 'title') });
      this[types.CLOSE_MODAL]({ id: this.id });
    },
    imageUrl(template) {
      // clean up the name
      const cleanStr = template.name.replace(/[^a-z]/gi, '_').toLowerCase();
      const url = `${cleanStr}.png`;

      try {
        // eslint-disable-next-line global-require
        const image = require(`@/assets/templates/${url}`);
        return image;
      } catch {
        return '';
      }
    },
  },
};
</script>

<style lang="stylus">
.modal--add-slide .modal__heading {
  nebula-text-h4();
}

.add-slide-modal {
  &__loading {
    bottom: $nebula-space-4x;
    top: $nebula-space-10x;
  }

  &__subheading {
    align-items: flex-end;
    display: flex;
    margin-bottom: $nebula-space-1x;

    h4 {
      nebula-text-h5();
      font-weight: 400;
      margin-block: 0;
      margin-inline: 0 $nebula-space-1x;
    }
  }

  &__quiz-item-form {
    align-items: flex-start;
    display: flex;
    margin-bottom: $nebula-space-2x;

    .nebula-input {
      margin-bottom: 0;
      margin-inline-end: $nebula-space-2x;
    }

    .nebula-field-group {
      flex: 1;
      margin-bottom: 0;
      margin-right: $nebula-space-2x;
      max-width: 500px;
    }
  }

  &__media-list {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: $nebula-space-2x;
    row-gap: $nebula-space-1x;
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;
  }

  &__item {
    display: flex;
    flex-direction: column;
    flex-grow: 0;
    justify-content: space-between;
    list-style-type: none;
    margin-block: 0 $nebula-space-2x;
    margin-inline: 0 $nebula-space-4x;
    width: "calc((100% - %s * 2) / 3)" % $nebula-space-4x;

    &:nth-child(3n) {
      margin-inline-end: 0;
    }
  }

  &__preview-wrapper {
    background: none;
    border: 0;
    border-radius: 0;
    display: block;
    margin: $nebula-space-half 0 0;
    padding: 0;
    width: 100%;
  }

  &__preview-image {
    display: block;
    width: 100%;
  }

  html[dir="rtl"] & {
    &__preview-image {
      transform: scaleX(-1);
    }
  }

  @media screen and (min-width: $comet-breakpoints-tablet-landscape) {
    &__media-list {
      flex-wrap: nowrap;
    }
  }
}
</style>
